import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { BUSINESS, NAV } from "../data/site";
import { useRequestModal } from "./RequestModal";
import { track } from "../lib/api";

function DesktopDropdown({ item }) {
  return (
    <div className="relative group">
      <Link
        to={item.to}
        className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-sky-700 transition-colors py-2"
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {item.label}
        <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
      </Link>
      <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-1 group-hover:translate-y-0">
        <div className="bg-white rounded-lg shadow-xl border border-slate-200 py-2 min-w-[220px]">
          {item.children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700 transition-colors"
              data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileMenu({ open, onClose }) {
  const [expanded, setExpanded] = useState(null);
  const { openRequest } = useRequestModal();
  const location = useLocation();
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="mobile-menu">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <img src={BUSINESS.logoLight} alt="Yavapai Waste Solutions" className="h-9 w-auto" />
        <button onClick={onClose} aria-label="Close menu" data-testid="mobile-menu-close" className="p-2 text-slate-700">
          <X className="h-6 w-6" />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto px-4 py-4">
        {NAV.filter((n) => n.label !== "Home").map((item) =>
          item.children ? (
            <div key={item.label} className="border-b border-slate-100">
              <button
                className="w-full flex items-center justify-between py-4 text-left font-heading font-semibold text-lg text-slate-900"
                onClick={() => setExpanded(expanded === item.label ? null : item.label)}
                aria-expanded={expanded === item.label}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
                <ChevronDown className={`h-5 w-5 transition-transform ${expanded === item.label ? "rotate-180" : ""}`} />
              </button>
              {expanded === item.label && (
                <div className="pb-3 pl-3 space-y-1">
                  <Link to={item.to} className="block py-2 text-slate-600" data-testid="mobile-nav-front-load-overview">
                    Front-Load Overview
                  </Link>
                  {item.children.map((c) => (
                    <Link key={c.to} to={c.to} className="block py-2 text-slate-600" data-testid={`mobile-nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Link
              key={item.to}
              to={item.to}
              className="block py-4 border-b border-slate-100 font-heading font-semibold text-lg text-slate-900"
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label}
            </Link>
          )
        )}
      </nav>
      <div className="p-4 space-y-3 border-t border-slate-200">
        <a
          href={BUSINESS.phoneHref}
          onClick={() => track("click_to_call", { location: "mobile_menu" })}
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-lg border-2 border-slate-900 font-heading font-semibold text-slate-900"
          data-testid="mobile-menu-call"
        >
          <Phone className="h-4 w-4" /> {BUSINESS.phone}
        </a>
        <button
          onClick={() => { onClose(); openRequest(); }}
          className="w-full py-3.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-heading font-semibold transition-colors"
          data-testid="mobile-menu-request"
        >
          Request a Dumpster
        </button>
      </div>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { openRequest } = useRequestModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[70] focus:bg-white focus:px-4 focus:py-2 focus:text-sky-700">
        Skip to content
      </a>
      <header
        className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 transition-all duration-300 ${scrolled ? "shadow-sm" : ""}`}
        data-testid="site-header"
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4 transition-all duration-300 ${scrolled ? "py-2" : "py-3.5"}`}>
          <Link to="/" aria-label="Yavapai Waste Solutions home" data-testid="header-logo">
            <img
              src={BUSINESS.logoLight}
              alt="Yavapai Waste Solutions"
              className={`w-auto transition-all duration-300 ${scrolled ? "h-12" : "h-16"}`}
            />
          </Link>
          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
            {NAV.filter((n) => n.label !== "Home").map((item) =>
              item.children ? (
                <DesktopDropdown key={item.label} item={item} />
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors py-2 ${isActive ? "text-sky-700" : "text-slate-700 hover:text-sky-700"}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={BUSINESS.phoneHref}
              onClick={() => track("click_to_call", { location: "header" })}
              className="hidden md:flex items-center gap-2 text-sm font-heading font-semibold text-slate-900 hover:text-sky-700 transition-colors"
              data-testid="header-phone"
            >
              <Phone className="h-4 w-4 text-sky-600" />
              {BUSINESS.phone}
            </a>
            <button
              onClick={() => openRequest()}
              className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 active:scale-[0.98] text-white text-sm font-heading font-semibold tracking-wide transition-all"
              data-testid="header-request-dumpster"
            >
              Request a Dumpster
            </button>
            <button
              className="lg:hidden p-2 text-slate-900"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
